import { Link } from '@tanstack/react-router';
import { DocsLayout } from '../../components/docs/DocsLayout.tsx';
import { useDocumentTitle } from '../../hooks/useDocumentTitle.ts';
import { useT } from '../../i18n.ts';

export function DocsTeams() {
  const t = useT();
  useDocumentTitle(t('docs.teams.title'));

  return (
    <DocsLayout
      path="/docs/teams"
      title={t('docs.teams.label')}
      lede="A team is a named group of people in your workspace. Teams are how you grant document access and how approvals are organized. This page covers creating teams, adding them to documents, managing members, and setting each team’s approval rule."
    >
      <h2 id="intro">About teams</h2>
      <p>Teams do two jobs in Goalpost:</p>
      <ul>
        <li>
          <strong>Access.</strong> Adding a team to a document is what lets its
          members see and work on that document. Access always flows through
          teams, never through individuals.
        </li>
        <li>
          <strong>Approvals.</strong> A team is the unit of sign-off. Documents
          can be configured to show which teams need to sign off on changes, and
          teams themselves have configurable rules for how team approval is
          granted. For instance, one team might decide that any one of its
          members can sign off on changes on behalf of the team, and another
          might require all its members to approve before the team’s approval is
          granted.
        </li>
      </ul>
      <p>
        A team of one is completely normal, such as a solo contractor or an
        individual tech lead. Teams live at the workspace level, so the same team
        can be added to multiple documents without having to recreate it.
      </p>

      <h2 id="create">Create a team</h2>
      <ol>
        <li>
          Go to <strong>Teams</strong> and choose{' '}
          <strong>Create New Team</strong>.
        </li>
        <li>
          Give it a clear name that describes the group, since you’ll refer to
          it in approval rules.
        </li>
        <li>
          Create it, then invite members and set its approval rule (both below).
        </li>
      </ol>

      <h2 id="add-to-document">Add a team to a document</h2>
      <p>
        Teams are added to a document from the team’s side. Assigning a team
        grants every one of its members access to the whole document and its
        history.
      </p>
      <ol>
        <li>Go to <strong>Teams</strong> and select the team.</li>
        <li>Open the <strong>Documents</strong> tab.</li>
        <li>Pick a document from the list and choose <strong>Assign</strong>.</li>
      </ol>
      <p>
        The team’s members can now open the document immediately, and the team
        becomes available to name in the document’s{' '}
        <Link to="/docs/approval-rules">approval rule</Link>.
      </p>

      <h2 id="remove-from-document">Remove a team from a document</h2>
      <p>
        You can remove a team from a document from either side. From the team,
        open its <strong>Documents</strong> tab and choose <strong>Remove</strong>{' '}
        next to the document. From the document, select <strong>Settings</strong>{' '}
        in the document nav, find the <strong>Teams</strong> section, and choose{' '}
        <strong>Remove</strong>. Members lose access unless they reach the
        document through another team they’re on.
      </p>
      <p>
        A team that is named in the document’s approval rule can’t be removed
        while it’s still part of that rule. Take the team out of the rule
        first, then remove it from the document. Goalpost disables the remove
        control until you do, and tells you to remove the team from the approval
        rule first.
      </p>

      <h2 id="archive">Archive a team</h2>
      <p>
        Archiving retires a team from new use without erasing it. An archived
        team stays visible in approval history and everywhere it’s already
        referenced, but it can’t be added to an approval rule or assigned to
        a new document. Archive a team when its work is done but you want to keep
        the record, rather than deleting it outright.
      </p>
      <p>
        A team must be removed from all its documents and approval rules
        before it can be archived.
      </p>
      <ol>
        <li>Go to <strong>Teams</strong> and select the team.</li>
        <li>Open the <strong>Settings</strong> tab.</li>
        <li>
          In the <strong>Archive Team</strong> section, choose{' '}
          <strong>Archive Team</strong>. You can bring it back later with{' '}
          <strong>Unarchive Team</strong>.
        </li>
      </ol>

      <h2 id="delete">Delete a team</h2>
      <p>
        Deleting a team removes it and all of its memberships permanently, and
        can’t be undone. It doesn’t remove the people in it — only their
        membership in this team.
      </p>
      <p>
        You can only delete teams with no history to preserve. A team that is
        named in an approval rule, has a pending approval, or appears anywhere
        in approval history can’t be deleted. For a team which has history, to
        block it from further use, archive it (see above).
      </p>
      <ol>
        <li>Go to <strong>Teams</strong> and select the team.</li>
        <li>Open the <strong>Settings</strong> tab.</li>
        <li>
          In the <strong>Delete Team</strong> section, choose{' '}
          <strong>Delete Team</strong> and confirm.
        </li>
      </ol>

      <h2 id="rules">Team approval rules</h2>
      <p>
        Each team has its own rule for what counts as “this team approved,”
        independent of any document it’s on. Set it on the team’s{' '}
        <strong>Approval</strong> tab:
      </p>
      <ul>
        <li>
          <strong>Any one member:</strong> a single member’s approval satisfies
          the team.
        </li>
        <li>
          <strong>All members:</strong> every member must approve.
        </li>
        <li>
          <strong>A set number of members:</strong> you choose how many of the
          team’s members must approve. (In the app this option is labeled “At
          least N members,” where you enter the number.)
        </li>
      </ul>
      <p>
        This rule is one half of how approvals work; the other half is how a
        document’s rule combines its teams. See{' '}
        <Link to="/docs/approval-rules">Approval Rules</Link> for how
        the two layers fit together.
      </p>

      <h2 id="invite">Invite someone to a team</h2>
      <p>
        You invite people to a team with an invitation link, not by email.
      </p>
      <ol>
        <li>Go to <strong>Teams</strong> and select the team.</li>
        <li>Open the <strong>Invites</strong> tab.</li>
        <li>
          Choose <strong>Generate Invitation Code</strong>, then{' '}
          <strong>Copy Link</strong> and send it to the person you’re inviting.
        </li>
      </ol>
      <p>
        Opening the link adds them to your workspace and to this team. You can{' '}
        <strong>Revoke</strong> a code at any time if you no longer want it used.
        Joining a team is what gives someone access to every document the team is
        on.
      </p>

      <h2 id="remove-member">Remove someone from a team</h2>
      <ol>
        <li>Go to <strong>Teams</strong> and select the team.</li>
        <li>Open the <strong>Members</strong> tab.</li>
        <li>Choose <strong>Remove</strong> next to the person, and confirm.</li>
      </ol>
      <p>
        They immediately lose the access that team provided, though if they’re on
        another team that’s also on a given document, they keep access through
        that one. Their past approvals stay in the document’s history; removing
        someone from a team never rewrites the record of what they previously
        signed off on.
      </p>
    </DocsLayout>
  );
}
